import { useEffect, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { motion, useInView } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';
import { ArrowLeft, ArrowRight, Users, Award, TrendingUp, CheckCircle } from 'lucide-react';

// Project data
const projectsData = {
  'tesla': {
    slug: 'tesla',
    title: 'Lancement Produit Tesla',
    tag: { fr: 'Lancement de marque', en: 'Brand Launch' },
    heroImage: 'https://images.unsplash.com/photo-1540575467063-178a50c2df87?auto=format&fit=crop&w=1600&q=80',
    intro: {
      fr: 'Événement exclusif de lancement avec expérience immersive. Coverage médiatique internationale et engagement digital record.',
      en: 'Exclusive launch event with immersive experience. International media coverage and record digital engagement.'
    },
    stats: [
      { value: '300', label: { fr: 'Invités', en: 'Guests' } },
      { value: '99%', label: { fr: 'Satisfaction', en: 'Satisfaction' } },
      { value: '+520%', label: { fr: 'ROI', en: 'ROI' } },
    ],
    description: {
      fr: 'BAO AGENCY a orchestré un événement de lancement immersif pour Tesla, réunissant 300 invités triés sur le volet dans un lieu confidentiel de prestige. Scénographie sur mesure, activation digitale live, et couverture presse internationale ont fait de cet événement une référence dans le secteur.',
      en: 'BAO AGENCY orchestrated an immersive launch event for Tesla, bringing together 300 handpicked guests in a prestigious confidential venue. Custom scenography, live digital activation, and international press coverage made this event a benchmark in the industry.'
    },
    gallery: [
      'https://images.unsplash.com/photo-1505373877841-8d25f7d46678?auto=format&fit=crop&w=600&q=80',
      'https://images.unsplash.com/photo-1475721027785-f74eccf877e2?auto=format&fit=crop&w=600&q=80',
      'https://images.unsplash.com/photo-1591115765373-5207764f72e7?auto=format&fit=crop&w=600&q=80',
    ],
    results: {
      fr: [
        'Couverture dans 12 médias nationaux',
        '+520% de retour sur investissement',
        'Tendance Twitter pendant 6h',
        '2M d\'impressions sur les réseaux sociaux',
      ],
      en: [
        'Coverage in 12 national media outlets',
        '+520% return on investment',
        'Twitter trending for 6 hours',
        '2M social media impressions',
      ]
    }
  },
  'gala-magazine': {
    slug: 'gala-magazine',
    title: 'Soirée Gala Magazine',
    tag: { fr: 'Soirée de prestige', en: 'Prestigious Evening' },
    heroImage: 'https://images.unsplash.com/photo-1511795409834-ef04bbd61622?auto=format&fit=crop&w=1600&q=80',
    intro: {
      fr: 'Soirée de prestige réunissant influenceurs et personnalités. Couverture presse nationale et viralité digitale record.',
      en: 'Prestigious evening bringing together influencers and personalities. National press coverage and record digital virality.'
    },
    stats: [
      { value: '500', label: { fr: 'Invités', en: 'Guests' } },
      { value: '15', label: { fr: 'Médias présents', en: 'Media present' } },
      { value: '+350%', label: { fr: 'ROI', en: 'ROI' } },
    ],
    description: {
      fr: 'Pour le lancement de leur nouvelle édition, Gala Magazine a fait confiance à BAO AGENCY pour organiser une soirée exclusive à Paris. Red carpet, DJ set premium, et activation Instagram live ont créé un événement mémorable relayé par les plus grands médias.',
      en: 'For the launch of their new edition, Gala Magazine trusted BAO AGENCY to organize an exclusive evening in Paris. Red carpet, premium DJ set, and live Instagram activation created a memorable event covered by the biggest media outlets.'
    },
    gallery: [
      'https://images.unsplash.com/photo-1492684223066-81342ee5ff30?auto=format&fit=crop&w=600&q=80',
      'https://images.unsplash.com/photo-1514525253161-7a46d19cd819?auto=format&fit=crop&w=600&q=80',
      'https://images.unsplash.com/photo-1533174072545-7a4b6ad7a6c3?auto=format&fit=crop&w=600&q=80',
    ],
    results: {
      fr: [
        '500 personnalités et influenceurs présents',
        '15 médias nationaux présents',
        '8M d\'impressions Instagram en 48h',
        '+350% ROI pour le client',
      ],
      en: [
        '500 personalities and influencers present',
        '15 national media outlets present',
        '8M Instagram impressions in 48h',
        '+350% ROI for the client',
      ]
    }
  },
  'la-maison': {
    slug: 'la-maison',
    title: 'Inauguration La Maison',
    tag: { fr: 'Ouverture exclusive', en: 'Exclusive Opening' },
    heroImage: 'https://images.unsplash.com/photo-1519167758481-83f550bb49b3?auto=format&fit=crop&w=1600&q=80',
    intro: {
      fr: 'Ouverture exclusive d\'un établissement haut de gamme avec activation digitale live et couverture presse.',
      en: 'Exclusive opening of a high-end establishment with live digital activation and press coverage.'
    },
    stats: [
      { value: '200', label: { fr: 'Invités', en: 'Guests' } },
      { value: '98%', label: { fr: 'Satisfaction', en: 'Satisfaction' } },
      { value: '+280%', label: { fr: 'ROI', en: 'ROI' } },
    ],
    description: {
      fr: 'L\'inauguration de La Maison était un moment clé pour positionner cet établissement haut de gamme sur le marché. BAO AGENCY a conçu une soirée d\'ouverture sur mesure, alliant élégance, storytelling de marque et activation digitale pour créer le buzz dès le premier soir.',
      en: 'The inauguration of La Maison was a key moment to position this high-end establishment in the market. BAO AGENCY designed a bespoke opening evening, combining elegance, brand storytelling and digital activation to create buzz from the very first night.'
    },
    gallery: [
      'https://images.unsplash.com/photo-1530103862676-de8c9debad1d?auto=format&fit=crop&w=600&q=80',
      'https://images.unsplash.com/photo-1464366400600-7168b8af9bc3?auto=format&fit=crop&w=600&q=80',
      'https://images.unsplash.com/photo-1478146896981-b80fe463b330?auto=format&fit=crop&w=600&q=80',
    ],
    results: {
      fr: [
        '200 invités VIP et presse',
        'Sold out dès la première semaine post-inauguration',
        '+280% ROI',
        'Mention dans Le Figaro et Vogue France',
      ],
      en: [
        '200 VIP and press guests',
        'Sold out within the first week post-inauguration',
        '+280% ROI',
        'Featured in Le Figaro and Vogue France',
      ]
    }
  }
};

const ProjectPage = () => {
  const { projectSlug } = useParams();
  const { language } = useLanguage();
  const navigate = useNavigate();
  const statsRef = useRef(null);
  const galleryRef = useRef(null);
  const resultsRef = useRef(null);
  const statsInView = useInView(statsRef, { once: true, margin: "-100px" });
  const galleryInView = useInView(galleryRef, { once: true, margin: "-100px" });
  const resultsInView = useInView(resultsRef, { once: true, margin: "-100px" });

  const project = projectsData[projectSlug];

  // Scroll to top on page load
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [projectSlug]);

  if (!project) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="font-serif text-4xl mb-4">
            {language === 'fr' ? 'Projet non trouvé' : 'Project not found'}
          </h1>
          <Link to="/" className="btn-pill bg-black text-white px-8 py-3">
            {language === 'fr' ? 'Retour à l\'accueil' : 'Back to home'}
          </Link>
        </div>
      </div>
    );
  }

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1 }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0 }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.5 }}
      className="min-h-screen bg-white"
      data-testid="project-page"
    >
      {/* Back Button */}
      <div className="container-custom pt-24 pb-6">
        <motion.button
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.2 }}
          onClick={() => navigate('/#realisations')}
          className="inline-flex items-center gap-2 text-neutral-600 hover:text-black transition-colors group"
          data-testid="back-button"
        >
          <ArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform" />
          <span className="font-medium">
            {language === 'fr' ? 'Retour aux réalisations' : 'Back to projects'}
          </span>
        </motion.button>
      </div>

      {/* Hero Section */}
      <section className="relative">
        {/* Hero Image */}
        <motion.div
          initial={{ opacity: 0, scale: 1.05 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8 }}
          className="relative h-[50vh] md:h-[60vh] overflow-hidden"
        >
          <img
            src={project.heroImage}
            alt={project.title}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent" />
          
          {/* Tag Badge */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="absolute top-6 left-6 md:top-10 md:left-10 bg-white/95 backdrop-blur-sm px-5 py-2 rounded-full text-sm font-semibold text-black"
          >
            {project.tag[language]}
          </motion.div>

          {/* Title Overlay */}
          <div className="absolute bottom-0 left-0 right-0 p-6 md:p-10">
            <div className="container-custom">
              <motion.h1
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4, duration: 0.6 }}
                className="font-serif text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4"
                data-testid="project-title"
              >
                {project.title}
              </motion.h1>
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5, duration: 0.6 }}
                className="text-lg md:text-xl text-white/90 max-w-3xl"
              >
                {project.intro[language]}
              </motion.p>
            </div>
          </div>
        </motion.div>
      </section>

      {/* Stats Section */}
      <section ref={statsRef} className="py-16 md:py-20 bg-neutral-50">
        <div className="container-custom">
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate={statsInView ? "visible" : "hidden"}
            className="grid grid-cols-3 gap-6 md:gap-10"
          >
            {project.stats.map((stat, index) => (
              <motion.div
                key={index}
                variants={itemVariants}
                className="text-center"
                data-testid={`project-stat-${index}`}
              >
                <div className="font-serif text-4xl md:text-5xl lg:text-6xl font-bold text-black mb-2">
                  {stat.value}
                </div>
                <div className="text-sm md:text-base text-neutral-500 uppercase tracking-wider">
                  {stat.label[language]}
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Description Section */}
      <section className="py-16 md:py-24">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="font-serif text-2xl md:text-3xl font-bold text-black mb-6">
                {language === 'fr' ? 'À propos du projet' : 'About the project'}
              </h2>
              <p className="text-lg md:text-xl text-neutral-600 leading-relaxed">
                {project.description[language]}
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Photo Gallery */}
      <section ref={galleryRef} className="py-16 md:py-20 bg-neutral-50">
        <div className="container-custom">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            animate={galleryInView ? { opacity: 1, y: 0 } : {}}
            className="font-serif text-2xl md:text-3xl font-bold text-black mb-10 text-center"
          >
            {language === 'fr' ? 'Galerie photos' : 'Photo gallery'}
          </motion.h2>
          
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate={galleryInView ? "visible" : "hidden"}
            className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6"
          >
            {project.gallery.map((image, index) => (
              <motion.div
                key={index}
                variants={itemVariants}
                whileHover={{ scale: 1.02 }}
                className="relative aspect-[4/3] rounded-2xl overflow-hidden shadow-lg"
                data-testid={`gallery-image-${index}`}
              >
                <img
                  src={image}
                  alt={`${project.title} - ${index + 1}`}
                  className="w-full h-full object-cover"
                />
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Results Section */}
      <section ref={resultsRef} className="py-16 md:py-24">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              animate={resultsInView ? { opacity: 1, y: 0 } : {}}
              className="font-serif text-2xl md:text-3xl font-bold text-black mb-10"
            >
              {language === 'fr' ? 'Résultats obtenus' : 'Results achieved'}
            </motion.h2>

            <motion.div
              variants={containerVariants}
              initial="hidden"
              animate={resultsInView ? "visible" : "hidden"}
              className="space-y-4"
            >
              {project.results[language].map((result, index) => (
                <motion.div
                  key={index}
                  variants={itemVariants}
                  className="flex items-start gap-4 p-4 bg-neutral-50 rounded-xl"
                  data-testid={`result-item-${index}`}
                >
                  <div className="flex-shrink-0 w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                  </div>
                  <span className="text-lg text-neutral-700">{result}</span>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24 bg-black">
        <div className="container-custom text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6">
              {language === 'fr' 
                ? 'Vous avez un projet similaire ?' 
                : 'Have a similar project?'}
            </h2>
            <p className="text-lg text-white/70 mb-10 max-w-2xl mx-auto">
              {language === 'fr'
                ? 'Contactez-nous pour discuter de votre événement et découvrir comment BAO AGENCY peut vous accompagner.'
                : 'Contact us to discuss your event and discover how BAO AGENCY can support you.'}
            </p>
            <motion.a
              href="/#contact"
              whileHover={{ scale: 1.05, boxShadow: '0 20px 40px rgba(255,255,255,0.1)' }}
              whileTap={{ scale: 0.95 }}
              className="inline-flex items-center gap-3 btn-pill bg-white text-black font-semibold px-10 py-5 text-lg"
              data-testid="project-cta"
            >
              {language === 'fr' ? 'Créer un événement similaire' : 'Create a similar event'}
              <ArrowRight className="w-5 h-5" />
            </motion.a>
          </motion.div>
        </div>
      </section>

      {/* Other Projects */}
      <section className="py-16 md:py-24">
        <div className="container-custom">
          <h2 className="font-serif text-2xl md:text-3xl font-bold text-black mb-10 text-center">
            {language === 'fr' ? 'Autres réalisations' : 'Other projects'}
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {Object.values(projectsData)
              .filter(p => p.slug !== projectSlug)
              .map((otherProject, index) => (
                <motion.div
                  key={otherProject.slug}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  whileHover={{ y: -5 }}
                >
                  <Link
                    to={`/projets/${otherProject.slug}`}
                    className="block group"
                    data-testid={`other-project-${otherProject.slug}`}
                  >
                    <div className="relative aspect-[16/9] rounded-2xl overflow-hidden mb-4">
                      <img
                        src={otherProject.heroImage}
                        alt={otherProject.title}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                      <div className="absolute inset-0 bg-black/30 group-hover:bg-black/40 transition-colors" />
                      <div className="absolute top-4 left-4 bg-white/90 px-3 py-1 rounded-full text-xs font-semibold">
                        {otherProject.tag[language]}
                      </div>
                    </div>
                    <h3 className="font-serif text-xl font-bold text-black group-hover:text-neutral-600 transition-colors">
                      {otherProject.title}
                    </h3>
                  </Link>
                </motion.div>
              ))}
          </div>
        </div>
      </section>
    </motion.div>
  );
};

export default ProjectPage;
